import React, { useState, useRef, useEffect } from 'react';
import './LeadInActivitiesExercise.css';
import { fetchOpenAIText } from '../../utils/openaiClient';

const LeadInActivitiesExercise = ({ text, title, level, activities, onClose, updateAttributes, node, editor, getPos , isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [editableText, setEditableText] = useState(text || '');
  const [editableActivities, setEditableActivities] = useState([...activities]);
  const [isGenerating, setIsGenerating] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showAddMenu, setShowAddMenu] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const addBtnRef = useRef(null);

  // Live activity execution — teacher-driven, mirrored to the student via
  // updateState/otherState (see ExerciseSyncContext). liveActivity holds at
  // most one running/paused activity at a time:
  //   { index, status: 'running'|'paused', endsAt, pausedRemaining }
  // endsAt is an absolute timestamp so remaining time is always recomputed
  // from the wall clock (both sides, on every tick, and on remount) rather
  // than decremented locally — that way a page reload or a student opening
  // the exercise mid-activity still shows the correct time left, with no
  // drift between teacher and student.
  const [liveActivity, setLiveActivity] = useState(null);
  const [completedIndices, setCompletedIndices] = useState({});
  const [displayRemaining, setDisplayRemaining] = useState(0);
  // Personal scratch space — intentionally local only, never sent via
  // updateState. This is warm-up thinking, not a graded/collected answer.
  const [activityResponses, setActivityResponses] = useState({});

  // Hydrate on mount: teacher restores their own last-broadcast state,
  // student mirrors the teacher's last known state (e.g. after a reload).
  useEffect(() => {
    const live = isStudent ? otherState.liveActivity : myState.liveActivity;
    if (live) setLiveActivity(live);
    const done = isStudent ? otherState.completedIndices : myState.completedIndices;
    if (done) setCompletedIndices(done);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Student: mirror the teacher's live control changes in real time.
  useEffect(() => {
    if (!isStudent) return;
    setLiveActivity(otherState.liveActivity || null);
  }, [isStudent, otherState.liveActivity]);

  useEffect(() => {
    if (!isStudent) return;
    if (otherState.completedIndices) setCompletedIndices(otherState.completedIndices);
  }, [isStudent, otherState.completedIndices]);

  // Tick the displayed countdown from the absolute endsAt timestamp.
  useEffect(() => {
    if (!liveActivity || liveActivity.status !== 'running' || !liveActivity.endsAt) {
      setDisplayRemaining(0);
      return;
    }
    const tick = () => {
      setDisplayRemaining(Math.max(0, Math.round((liveActivity.endsAt - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [liveActivity?.status, liveActivity?.endsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeRemaining = liveActivity?.status === 'paused'
    ? (liveActivity.pausedRemaining || 0)
    : displayRemaining;

  const broadcastLive = (next) => {
    setLiveActivity(next);
    updateState({ liveActivity: next });
  };

  // State for collapsible text content
  const [isTextContentExpanded, setIsTextContentExpanded] = useState(false);
  
  // State for adding more activities
  const [isAddingMore, setIsAddingMore] = useState(false);
  
  // State for editing text content
  const [isEditingText, setIsEditingText] = useState(false);
  const [tempText, setTempText] = useState('');

  // eslint-disable-next-line no-unused-vars
  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        text: editableText,
        title: editableTitle,
        level: editableLevel,
        activities: editableActivities,
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableLevel(level);
    setEditableText(text || '');
    setEditableActivities([...activities]);
  };

  const updateActivityField = (index, field, value) => {
    setEditableActivities(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddEmpty = () => {
    const newActivity = { title: '', description: '', type: 'discussion' };
    setEditableActivities(prev => [...prev, newActivity]);
    setShowAddMenu(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteActivity = (activityIndex) => {
    if (editableActivities.length <= 1) return;
    
    setEditableActivities(prev => prev.filter((_, index) => index !== activityIndex));
  };

  // Activity execution — teacher-only controls, broadcast to the student.
  const startActivity = (activityIndex) => {
    const activity = editableActivities[activityIndex];
    let endsAt = null;
    if (activity.duration) {
      const durationMatch = activity.duration.match(/(\d+)/);
      if (durationMatch) {
        endsAt = Date.now() + parseInt(durationMatch[1], 10) * 60000;
      }
    }
    broadcastLive({ index: activityIndex, status: 'running', endsAt, pausedRemaining: null });
  };

  const completeActivity = (activityIndex) => {
    setCompletedIndices(prev => {
      const next = { ...prev, [activityIndex]: true };
      updateState({ completedIndices: next });
      return next;
    });
    broadcastLive(null);
  };

  const pauseActivity = () => {
    if (!liveActivity || liveActivity.status !== 'running') return;
    const remaining = liveActivity.endsAt
      ? Math.max(0, Math.round((liveActivity.endsAt - Date.now()) / 1000))
      : null;
    broadcastLive({ ...liveActivity, status: 'paused', pausedRemaining: remaining });
  };

  const resumeActivity = () => {
    if (!liveActivity || liveActivity.status !== 'paused') return;
    const endsAt = liveActivity.pausedRemaining != null
      ? Date.now() + liveActivity.pausedRemaining * 1000
      : null;
    broadcastLive({ ...liveActivity, status: 'running', endsAt, pausedRemaining: null });
  };

  const cancelActivity = () => {
    broadcastLive(null);
  };

  const updateActivityResponse = (activityIndex, response) => {
    setActivityResponses(prev => ({ ...prev, [activityIndex]: response }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGenerateActivities = async () => {
    const textToUse = isEditingText ? tempText : editableText;
    if (!textToUse.trim()) {
      alert('Please add some text first!');
      return;
    }
    
    // Проверяем длину текста и предупреждаем пользователя
    const textLength = textToUse.length;
    if (textLength > 2000) {
      const confirmGenerate = window.confirm(
        `Text is quite long (${textLength} characters). Generation may take longer. Continue?`
      );
      if (!confirmGenerate) return;
    }
    
    setIsGenerating(true);
    
    try {
      const levelDescriptions = {
        'A1': 'Beginner - Simple vocabulary, basic grammar, short sentences, visual aids',
        'A2': 'Elementary - Common vocabulary, present/past tenses, everyday topics',
        'B1': 'Intermediate - Complex vocabulary, various tenses, abstract concepts',
        'B2': 'Upper Intermediate - Advanced vocabulary, complex grammar, nuanced topics',
        'C1': 'Advanced - Sophisticated language, complex ideas, critical thinking',
        'C2': 'Mastery - Native-like proficiency, complex texts, academic language'
      };

      const prompt = `Create 4 lead-in activities for ONLINE CLASSES based on the following text. These activities should prepare students for reading the text and activate their prior knowledge in a virtual learning environment.

LEVEL: ${editableLevel} - ${levelDescriptions[editableLevel]}

For ${editableLevel} level students in ONLINE CLASSES, create activities that:
- Use appropriate vocabulary complexity
- Match the grammatical structures students know
- Include clear, step-by-step instructions for virtual implementation
- Work well with video conferencing tools (Zoom, Teams, Google Meet, etc.)
- Utilize digital tools and platforms (chat, breakout rooms, screen sharing, polls, etc.)
- Are engaging and interactive in an online setting
- Require minimal physical materials (use digital alternatives)
- Can be completed individually or in small virtual groups
- Include clear instructions for online facilitation

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "title": "Lead-in Activities for Online Classes",
  "level": "${editableLevel}",
  "text": "${textToUse}",
  "activities": [
    {
      "title": "Activity title here",
      "description": "Detailed step-by-step instructions for ONLINE implementation",
      "type": "vocabulary|prediction|discussion|brainstorming|visual|kinesthetic|collaborative",
      "duration": "5-10 minutes",
      "materials": "Digital tools and platforms needed (e.g., chat, breakout rooms, screen sharing, online whiteboard)",
      "instructions": [
        "Step 1: Clear instruction for online facilitation",
        "Step 2: Next step with digital tools",
        "Step 3: Final step with online interaction"
      ],
      "expectedOutcome": "What students should achieve in the online environment"
    }
  ]
}

        Text: ${textToUse}`;
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 3000,
      });
      
      if (content) {
        let contentStr = content.trim();
        try {
          // Remove markdown blocks if present
          if (contentStr.startsWith('```json')) {
            contentStr = contentStr.replace(/^```json\n/, '').replace(/\n```$/, '');
          } else if (contentStr.startsWith('```')) {
            contentStr = contentStr.replace(/^```\n/, '').replace(/\n```$/, '');
          }
          const parsed = JSON.parse(contentStr);
          
          if (parsed.activities && Array.isArray(parsed.activities)) {
            setEditableActivities(parsed.activities);
            // If we're editing text, save the changes and exit edit mode
            if (isEditingText) {
              setEditableText(tempText);
              setIsEditingText(false);
              setTempText('');
            }
          } else {
            console.error('Invalid Lead-in Activities exercise format:', parsed);
            alert('Error generating activities. Please try again.');
          }
        } catch (parseError) {
          console.error('Error parsing Lead-in Activities JSON:', parseError);
          console.error('Raw content:', contentStr);
          
          // Проверяем, не обрезан ли ответ из-за лимита токенов
          if (contentStr && !contentStr.includes('"expectedOutcome"')) {
            alert('Response was truncated due to length. Try shortening the text or try again.');
          } else {
            alert('Error parsing activities. Please try again.');
          }
        }
      } else {
        console.error('No content in OpenAI Lead-in Activities response');
        alert('Error generating activities. Please try again.');
      }
    } catch (e) {
      console.error("Error in handleGenerateActivities:", e);
      alert('Error generating activities. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Functions for editing text content
  const handleEditText = () => {
    setTempText(editableText);
    setIsEditingText(true);
  };

  const handleSaveText = () => {
    setEditableText(tempText);
    setIsEditingText(false);
  };

  const handleCancelTextEdit = () => {
    setTempText('');
    setIsEditingText(false);
  };

  const handleAddMoreActivities = async () => {
    const textToUse = isEditingText ? tempText : editableText;
    if (!textToUse.trim()) {
      alert('Please add some text first!');
      return;
    }
    
    setIsAddingMore(true);
    
    try {
      const levelDescriptions = {
        'A1': 'Beginner - Simple vocabulary, basic grammar, short sentences, visual aids',
        'A2': 'Elementary - Common vocabulary, present/past tenses, everyday topics',
        'B1': 'Intermediate - Complex vocabulary, various tenses, abstract concepts',
        'B2': 'Upper Intermediate - Advanced vocabulary, complex grammar, nuanced topics',
        'C1': 'Advanced - Sophisticated language, complex ideas, critical thinking',
        'C2': 'Mastery - Native-like proficiency, complex texts, academic language'
      };

      const prompt = `Create 2 additional lead-in activities for ONLINE CLASSES based on the following text. These should be different from typical activities and provide variety for virtual learning. The level should be ${editableLevel}.

LEVEL: ${editableLevel} - ${levelDescriptions[editableLevel]}

For ${editableLevel} level students in ONLINE CLASSES, create 2 NEW activities that:
- Are different from typical vocabulary, prediction, discussion activities
- Use appropriate vocabulary complexity
- Match the grammatical structures students know
- Include clear, step-by-step instructions for virtual implementation
- Work well with video conferencing tools (Zoom, Teams, Google Meet, etc.)
- Utilize digital tools and platforms (chat, breakout rooms, screen sharing, polls, online whiteboard, etc.)
- Are engaging and interactive in an online setting
- Require minimal physical materials (use digital alternatives)
- Can be completed individually or in small virtual groups
- Include clear instructions for online facilitation
- Provide variety and complement existing activities

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "activities": [
    {
      "title": "Activity title here",
      "description": "Detailed step-by-step instructions for ONLINE implementation",
      "type": "visual|kinesthetic|collaborative|creative|analytical|reflective",
      "duration": "5-10 minutes",
      "materials": "Digital tools and platforms needed (e.g., chat, breakout rooms, screen sharing, online whiteboard)",
      "instructions": [
        "Step 1: Clear instruction for online facilitation",
        "Step 2: Next step with digital tools",
        "Step 3: Final step with online interaction"
      ],
      "expectedOutcome": "What students should achieve in the online environment"
    }
  ]
}

Text: ${textToUse}`;
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 1000,
      });
      
      if (content) {
        try {
          let contentStr = content.trim();
          // Remove markdown blocks if present
          if (contentStr.startsWith('```json')) {
            contentStr = contentStr.replace(/^```json\n/, '').replace(/\n```$/, '');
          } else if (contentStr.startsWith('```')) {
            contentStr = contentStr.replace(/^```\n/, '').replace(/\n```$/, '');
          }
          const parsed = JSON.parse(contentStr);
          
          if (parsed.activities && Array.isArray(parsed.activities)) {
            setEditableActivities(prev => [...prev, ...parsed.activities]);
          } else {
            console.error('Invalid additional activities format:', parsed);
            alert('Error generating additional activities. Please try again.');
          }
        } catch (parseError) {
          console.error('Error parsing additional activities JSON:', parseError);
          console.error('Raw content:', content);
          alert('Error parsing additional activities. Please try again.');
        }
      } else {
        console.error('No content in OpenAI additional activities response');
        alert('Error generating additional activities. Please try again.');
      }
    } catch (e) {
      console.error("Error in handleAddMoreActivities:", e);
      alert('Error generating additional activities. Please try again.');
    } finally {
      setIsAddingMore(false);
    }
  };

  return (
    <div className="lead-in-activities-exercise">
      <div className="exercise-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 className="exercise-title" style={{ margin: 0 }}>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="title-input"
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>{editableTitle}</span>
            </div>
          )}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span className="level-badge">{editableLevel}</span>
          {onClose && (
            <button className="close-button" onClick={onClose} title="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="exercise-content">
        {/* Text Display (Read Mode) */}
        {!isEditing && editableText && (
          <div className="text-section">
            <div 
              className="collapsible-header" 
              onClick={() => setIsTextContentExpanded(!isTextContentExpanded)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <h3 style={{ margin: 0 }}>Text Content</h3>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ 
                  transform: isTextContentExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {isTextContentExpanded && (
              <div>
                {isEditingText ? (
                  <div>
            <textarea
                      value={tempText}
                      onChange={e => setTempText(e.target.value)}
              className="text-input"
              placeholder="Enter the text content here..."
              rows={6}
                      style={{ marginBottom: '12px' }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <button
                        onClick={handleSaveText}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelTextEdit}
                        style={{
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Cancel
                      </button>
                      {tempText !== editableText && (
                        <button
                          onClick={handleGenerateActivities}
                          disabled={isGenerating}
                          className="generate-activities-button"
                        >
                          {isGenerating ? 'Generating...' : 'Generate New Activities'}
                        </button>
                      )}
                    </div>
          </div>
                ) : (
                  <div>
            <div className="text-display">
              {editableText}
            </div>
                    <button
                      onClick={handleEditText}
                      style={{
                        background: 'transparent',
                        color: '#6c757d',
                        border: '1px solid #e9ecef',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginTop: '8px'
                      }}
                    >
                      Edit Text
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* Activities Display */}
        {editableActivities.length > 0 && (
          <div className="activities-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
              <h3>Suggested Activities</h3>
            </div>
            
            <div className="activities-list">
              {editableActivities.map((activity, index) => (
                <div key={index} className={`activity-item ${liveActivity?.index === index ? 'active' : ''}`}>
                  <div className="activity-header">
                    <div className="activity-type-badge" data-type={activity.type}>#{activity.type}</div>
                    {activity.duration && (
                      <div className="activity-duration">{activity.duration}</div>
                    )}
                  </div>
                  
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={activity.title}
                        onChange={e => updateActivityField(index, 'title', e.target.value)}
                        className="activity-title-input"
                        placeholder="Activity title..."
                      />
                      <textarea
                        value={activity.description}
                        onChange={e => updateActivityField(index, 'description', e.target.value)}
                        className="activity-description-input"
                        placeholder="Activity description..."
                        rows={3}
                      />
                      <input
                        type="text"
                        value={activity.duration || ''}
                        onChange={e => updateActivityField(index, 'duration', e.target.value)}
                        className="activity-duration-input"
                        placeholder="Duration (e.g., 5-10 minutes)"
                      />
                      <textarea
                        value={activity.materials || ''}
                        onChange={e => updateActivityField(index, 'materials', e.target.value)}
                        className="activity-materials-input"
                        placeholder="Required materials..."
                        rows={2}
                      />
                    </>
                  ) : (
                    <>
                      <h4 className="activity-title">{activity.title}</h4>
                      <p className="activity-description">{activity.description}</p>
                      
                      {activity.materials && (
                        <div className="activity-materials">
                          <strong>Materials needed:</strong> {activity.materials}
                        </div>
                      )}
                      
                      {activity.instructions && Array.isArray(activity.instructions) && (
                        <div className="activity-instructions">
                          <strong>Instructions:</strong>
                          <ol>
                            {activity.instructions.map((instruction, i) => (
                              <li key={i}>{instruction}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      
                      {activity.expectedOutcome && (
                        <div className="activity-outcome">
                          <strong>Expected outcome:</strong> {activity.expectedOutcome}
                        </div>
                      )}
                      
                      {/* Activity execution controls */}
                      <div className="activity-controls">
                        {completedIndices[index] ? (
                          <div className="activity-completed">
                            <span className="completed-icon">✓</span>
                            Activity completed!
                          </div>
                        ) : liveActivity?.index === index ? (
                          <div className="activity-active">
                            {liveActivity.status === 'paused' ? (
                              <div className="activity-timer">
                                Paused{timeRemaining > 0 ? ` — ${formatTime(timeRemaining)} left` : ''}
                                {!isStudent && <button onClick={resumeActivity} className="timer-btn">Resume</button>}
                              </div>
                            ) : timeRemaining > 0 && liveActivity.endsAt ? (
                              <div className="activity-timer">
                                Time remaining: {formatTime(timeRemaining)}
                                {!isStudent && <button onClick={pauseActivity} className="timer-btn">Pause</button>}
                              </div>
                            ) : liveActivity.endsAt ? (
                              <div className="activity-timer">Time's up!</div>
                            ) : null}

                            {/* Response area based on activity type */}
                            {activity.type === 'discussion' && (
                              <div className="activity-response">
                                <textarea
                                  value={activityResponses[index] || ''}
                                  onChange={e => updateActivityResponse(index, e.target.value)}
                                  placeholder="Write your discussion points here..."
                                  rows={4}
                                />
                              </div>
                            )}
                            
                            {activity.type === 'brainstorming' && (
                              <div className="activity-response">
                                <textarea
                                  value={activityResponses[index] || ''}
                                  onChange={e => updateActivityResponse(index, e.target.value)}
                                  placeholder="Brainstorm your ideas here..."
                                  rows={4}
                                />
                              </div>
                            )}
                            
                            {activity.type === 'vocabulary' && (
                              <div className="activity-response">
                                <textarea
                                  value={activityResponses[index] || ''}
                                  onChange={e => updateActivityResponse(index, e.target.value)}
                                  placeholder="Write down new vocabulary words and their meanings..."
                                  rows={4}
                                />
                              </div>
                            )}
                            
                            {activity.type === 'prediction' && (
                              <div className="activity-response">
                                <textarea
                                  value={activityResponses[index] || ''}
                                  onChange={e => updateActivityResponse(index, e.target.value)}
                                  placeholder="Write your predictions here..."
                                  rows={4}
                                />
                              </div>
                            )}
                            
                            {!isStudent && (
                              <div className="activity-actions">
                                <button
                                  onClick={() => completeActivity(index)}
                                  className="complete-activity-btn"
                                >
                                  Complete Activity
                                </button>
                                <button
                                  onClick={cancelActivity}
                                  className="cancel-activity-btn"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        ) : !isStudent ? (
                          <button
                            onClick={() => startActivity(index)}
                            className="start-activity-btn"
                            disabled={!!liveActivity}
                            title={liveActivity ? 'Finish or cancel the current activity first' : undefined}
                          >
                            Start Activity
                          </button>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add More Activities Button */}
            {!isEditing && editableText && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                <button
                  className="add-more-activities-button"
                  onClick={handleAddMoreActivities}
                  disabled={isAddingMore}
                >
                  {isAddingMore ? 'Adding...' : 'Add More Activities'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadInActivitiesExercise; 